import { useState, useEffect, useCallback } from 'react';
import { GOLD, DARK_BG, CARD_BG, BORDER, TEXT, MUTED, GREEN } from './constants';
import { today, uid, loadLS, saveLS, LS_KEYS, fetchMetalPrices } from './utils';
import {
  fetchTransactions, upsertTransaction, deleteTransaction as dbDeleteTransaction,
  fetchInvoices, upsertInvoice, deleteInvoice as dbDeleteInvoice,
  fetchImportedPosIds, addImportedPosId,
  exportAllData, importAllData,
} from './db';

import Login          from './components/Login';
import Dashboard      from './components/Dashboard';
import TransactionsTab from './components/Transactions';
import InvoicesTab    from './components/Invoices';
import InventoryTab   from './components/Inventory';
import KPIReports     from './components/KPIReports';
import Settings       from './components/Settings';
import POSSync        from './components/POSSync';
import { MetalsTicker } from './components/UI';

const DEFAULT_PASSWORD = 'AGC2025!';
const DEFAULT_SETTINGS = { password: DEFAULT_PASSWORD };

const TABS = [
  { id: 'dashboard',    label: 'Dashboard' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'invoices',     label: 'Invoices' },
  { id: 'pos',          label: '⟳ POS Sync' },
  { id: 'inventory',    label: 'Inventory' },
  { id: 'kpis',         label: 'KPI Reports' },
  { id: 'settings',     label: '⚙ Settings' },
];

export default function App() {
  const [authed,           setAuthed]           = useState(false);
  const [transactions,     setTransactions]     = useState([]);
  const [invoices,         setInvoices]         = useState([]);
  const [settings,         setSettings]         = useState(DEFAULT_SETTINGS);
  const [metalPrices,      setMetalPrices]      = useState({ Gold: null, Silver: null, Platinum: null, Palladium: null, ok: false, timestamp: '—' });
  const [activeTab,        setActiveTab]        = useState('dashboard');
  const [lastBackup,       setLastBackup]       = useState(null);
  const [importedAureusIds,setImportedAureusIds]= useState([]);
  const [syncStatus,       setSyncStatus]       = useState('syncing');

  // ── Initial load from Supabase ──────────────────────────────
  useEffect(() => {
    const set = loadLS(LS_KEYS.settings, DEFAULT_SETTINGS);
    const lb  = loadLS(LS_KEYS.lastBackup, null);
    setSettings(set);
    setLastBackup(lb);

    setSyncStatus('syncing');
    Promise.all([
      fetchTransactions(),
      fetchInvoices(),
      fetchImportedPosIds(),
    ]).then(([txns, invs, posIds]) => {
      setTransactions(txns);
      setInvoices(invs);
      setImportedAureusIds(posIds);
      setSyncStatus('synced');
    }).catch(() => {
      setTransactions(loadLS(LS_KEYS.transactions, []));
      setInvoices(loadLS(LS_KEYS.invoices, []));
      setImportedAureusIds(loadLS('agc_imported_pos_ids', []));
      setSyncStatus('offline');
    });
  }, []);

  useEffect(() => { saveLS(LS_KEYS.settings, settings); }, [settings]);

  useEffect(() => {
    fetchMetalPrices().then(setMetalPrices);
    const t = setInterval(() => fetchMetalPrices().then(setMetalPrices), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // ── Transaction CRUD ──────────────────────────────────────────
  function addTransaction(tx) {
    const entry = { ...tx, id: tx.id || uid(), createdAt: tx.createdAt || new Date().toISOString() };
    setTransactions((p) => [entry, ...p.filter(t => t.id !== entry.id)]);
    upsertTransaction(entry);
    return entry;
  }
  function removeTransaction(id) {
    setTransactions((p) => p.filter((t) => t.id !== id));
    dbDeleteTransaction(id);
  }
  function updateTransaction(updated) {
    setTransactions((p) => p.map((t) => t.id === updated.id ? { ...t, ...updated } : t));
    upsertTransaction(updated);
  }

  // ── Invoice CRUD ──────────────────────────────────────────────
  function addInvoice(inv) {
    const invEntry = { ...inv, id: inv.id || uid(), createdAt: inv.createdAt || new Date().toISOString() };
    setInvoices((p) => [invEntry, ...p.filter(i => i.id !== invEntry.id)]);
    upsertInvoice(invEntry);
    (inv.lineItems || []).forEach((li) => {
      addTransaction({
        date:          inv.date,
        direction:     inv.direction,
        channel:       inv.channel,
        paymentMethod: inv.paymentMethod || '',
        vendor:        inv.vendor,
        invoiceNumber: inv.invoiceNumber,
        invoiceId:     invEntry.id,
        aureusId:      inv.aureusId || null,
        category:      li.category,
        description:   li.description,
        metal:         li.metal,
        qty:           li.qty,
        unitPrice:     li.unitPrice,
        totalAmount:   li.qty * li.unitPrice,
        weight:        li.weight || 0,
        notes:         [inv.notes, `Invoice: ${inv.invoiceNumber}`].filter(Boolean).join(' | '),
        source:        'invoice',
      });
    });
    return invEntry;
  }
  function removeInvoice(id) {
    const linked = transactions.filter(t => t.invoiceId === id);
    setInvoices((p) => p.filter((inv) => inv.id !== id));
    setTransactions((p) => p.filter((t) => t.invoiceId !== id));
    dbDeleteInvoice(id);
    linked.forEach(t => dbDeleteTransaction(t.id));
  }

  // ── POS import ────────────────────────────────────────────────
  function importPOSOrder(order) {
    addInvoice(order);
    addImportedPosId(order.aureusId);
    setImportedAureusIds((p) => [...new Set([...p, order.aureusId])]);
  }

  // ── Backup / Restore ──────────────────────────────────────────
  async function handleExport() {
    const data = await exportAllData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `agc-backup-${today()}.json`; a.click();
    URL.revokeObjectURL(url);
    const lb = new Date().toLocaleString();
    setLastBackup(lb); saveLS(LS_KEYS.lastBackup, lb);
  }
  async function handleImport(data) {
    await importAllData(data);
    const [txns, invs, posIds] = await Promise.all([fetchTransactions(), fetchInvoices(), fetchImportedPosIds()]);
    setTransactions(txns); setInvoices(invs); setImportedAureusIds(posIds);
  }

  if (!authed) {
    return <Login onSuccess={() => setAuthed(true)} storedPassword={settings.password} />;
  }

  return (
    <div style={{ background: DARK_BG, minHeight: '100vh', color: TEXT, fontFamily: "'DM Mono', monospace" }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 32px', background: CARD_BG,
        borderBottom: `1px solid ${BORDER}`, position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 900, color: GOLD, lineHeight: 1 }}>AGC</span>
          <div>
            <div style={{ color: MUTED, fontSize: 9, letterSpacing: 3 }}>ATLANTA GOLD & COIN</div>
            <div style={{ color: MUTED, fontSize: 8, letterSpacing: 2, opacity: 0.6 }}>OPERATIONS · JOHNS CREEK, GA</div>
          </div>
        </div>
        <MetalsTicker prices={metalPrices} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontSize: 9, letterSpacing: 1, padding: '3px 8px', borderRadius: 4,
              background: syncStatus === 'synced' ? '#081A0D' : syncStatus === 'syncing' ? '#1A1500' : '#1A0808',
              color:      syncStatus === 'synced' ? GREEN     : syncStatus === 'syncing' ? '#F5C518' : '#FF6B6B',
              border:    `1px solid ${syncStatus === 'synced' ? GREEN+'40' : syncStatus === 'syncing' ? '#F5C51840' : '#FF6B6B40'}`,
            }}>
              {syncStatus === 'synced' ? '● SYNCED' : syncStatus === 'syncing' ? '⟳ SYNCING' : '○ OFFLINE'}
            </span>
            <button onClick={() => setActiveTab('settings')} style={{
              background: 'transparent', border: `1px solid ${BORDER}`, color: GOLD,
              padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
              fontSize: 11, fontFamily: "'DM Mono', monospace", letterSpacing: 0.5,
            }}>↓ Backup / Settings</button>
          </div>
          {lastBackup && <span style={{ color: MUTED, fontSize: 9 }}>Last backup: {lastBackup}</span>}
        </div>
      </header>

      <nav style={{ display: 'flex', gap: 2, padding: '0 32px', background: CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
        {TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '13px 20px', background: 'transparent', border: 'none',
            color: activeTab === tab.id ? TEXT : MUTED, cursor: 'pointer', fontSize: 12, letterSpacing: 0.5,
            borderBottom: `2px solid ${activeTab === tab.id ? GOLD : 'transparent'}`,
            fontFamily: "'DM Mono', monospace", transition: 'all 0.15s',
          }}>{tab.label}</button>
        ))}
      </nav>

      <main style={{ padding: '28px 32px', maxWidth: 1700, margin: '0 auto' }}>
        {activeTab === 'dashboard'    && <Dashboard    transactions={transactions} metalPrices={metalPrices} />}
        {activeTab === 'transactions' && <TransactionsTab transactions={transactions} onAdd={addTransaction} onDelete={removeTransaction} onUpdate={updateTransaction} />}
        {activeTab === 'invoices'     && <InvoicesTab   invoices={invoices} onAdd={addInvoice} onDelete={removeInvoice} />}
        {activeTab === 'pos'          && <POSSync       onImport={importPOSOrder} importedIds={importedAureusIds} />}
        {activeTab === 'inventory'    && <InventoryTab  transactions={transactions} metalPrices={metalPrices} />}
        {activeTab === 'kpis'         && <KPIReports    transactions={transactions} />}
        {activeTab === 'settings'     && (
          <Settings
            transactions={transactions} invoices={invoices} settings={settings}
            onSettingsChange={setSettings} lastBackup={lastBackup} setLastBackup={setLastBackup}
            onExport={handleExport} onImport={handleImport}
          />
        )}
      </main>
    </div>
  );
}
